import { useEffect, useRef, useState, useCallback } from 'react';
import type { CardTemplateData, CardTemplateElement } from '../../types/cardTemplate';

const BUILDER_IMG_WIDTH = 800;

interface FlatElement {
  element: CardTemplateElement;
  offsetX: number;
  offsetY: number;
  parentGroupWidth: number;
  parentGroupHeight: number;
}

function flattenElements(
  elements: CardTemplateElement[],
  canvasBuilderWidth: number,
  canvasBuilderHeight: number,
): FlatElement[] {
  const result: FlatElement[] = [];
  const inGroup = new Set<number>();

  elements.forEach(element => {
    if (element.type !== 'group') return;
    const groupW = element.widthType  === 'percent' ? element.width  * canvasBuilderWidth  / 100 : element.width;
    const groupH = element.heightType === 'percent' ? element.height * canvasBuilderHeight / 100 : element.height;
    const children = elements.filter(e => e.group_key === element.group_key && e.id !== element.id);
    children.forEach(child => {
      inGroup.add(child.id);
      result.push({ element: child, offsetX: element.x, offsetY: element.y, parentGroupWidth: groupW, parentGroupHeight: groupH });
    });
  });

  elements.forEach(element => {
    if (element.type === 'group') return;
    if (inGroup.has(element.id) || element.group_key) return;
    result.push({ element, offsetX: 0, offsetY: 0, parentGroupWidth: canvasBuilderWidth, parentGroupHeight: canvasBuilderHeight });
  });

  return result;
}

function applyForceCase(text: string, forceCase?: string): string {
  switch (forceCase) {
    case 'upper':    return text.toUpperCase();
    case 'lower':    return text.toLowerCase();
    case 'title':    return text.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case 'sentence': return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    default:         return text;
  }
}

function verticalAlignToFlex(align?: string): string {
  return align === 'bottom' ? 'flex-end' : align === 'middle' ? 'center' : 'flex-start';
}

function ElementRenderer({
  item,
  renderRatio,
  fieldValues,
}: {
  item: FlatElement;
  renderRatio: number;
  fieldValues: Record<string, string>;
}) {
  const { element, offsetX, offsetY, parentGroupWidth, parentGroupHeight } = item;
  const elementStyle = element.style;

  const elementWidthPx  = element.widthType  === 'percent' ? element.width  * parentGroupWidth  / 100 : element.width;
  const elementHeightPx = element.heightType === 'percent' ? element.height * parentGroupHeight / 100 : element.height;

  const x = Math.round((element.x + offsetX) * renderRatio);
  const y = Math.round((element.y + offsetY) * renderRatio);
  const w = Math.round(elementWidthPx * renderRatio);
  const h = Math.round(elementHeightPx * renderRatio);
  const fontSizePx = (elementStyle?.fontSize || 12) * renderRatio;

  const wrapStyle: React.CSSProperties = {
    position: 'absolute',
    left: x, top: y, width: w, height: h,
  };

  if (element.type === 'rectangle') {
    const color   = (element.data.color   as string | undefined) ?? '#4a90d9';
    const opacity = (element.data.opacity as number | undefined) ?? 100;
    return (
      <div style={{ ...wrapStyle, overflow: 'hidden' }}>
        <div style={{ width: '100%', height: '100%', background: color, opacity: opacity / 100 }} />
      </div>
    );
  }

  if (element.type === 'image') {
    const imageUrl = element.data.url as string | undefined;
    if (!imageUrl) return null;
    return (
      <div style={{ ...wrapStyle, overflow: 'hidden' }}>
        <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} crossOrigin="anonymous" />
      </div>
    );
  }

  if (element.type === 'field') {
    const fieldData = element.data as {
      placeholder?: string;
      sampleText?: string | null;
      prefix?: string | null;
      prefixStyle?: { color?: string; fontSize?: number; fontFamily?: string; bold?: boolean; italic?: boolean };
    };
    const userValue = fieldValues[String(element.id)] ?? '';
    const displayText = applyForceCase(userValue || fieldData.sampleText || '', elementStyle?.forceCase);
    const hasPrefix = !!fieldData.prefix;
    const textColor = userValue ? (elementStyle?.color || '#1e1e2e') : '#6c7086';
    const isItalic  = userValue ? (elementStyle?.italic || false) : true;
    const hAlignFlex = elementStyle?.textAlign === 'center' ? 'center' : elementStyle?.textAlign === 'right' ? 'flex-end' : 'flex-start';

    return (
      <div style={wrapStyle}>
        <div style={{
          width: '100%', height: '100%',
          display: 'flex',
          flexDirection: hasPrefix ? 'row' : 'column',
          alignItems:    hasPrefix ? verticalAlignToFlex(elementStyle?.verticalAlign) : undefined,
          justifyContent: hasPrefix ? hAlignFlex : verticalAlignToFlex(elementStyle?.verticalAlign),
          fontFamily:     elementStyle?.fontFamily || 'Inter',
          fontSize:       fontSizePx,
          color:          textColor,
          fontWeight:     elementStyle?.bold ? 'bold' : 'normal',
          fontStyle:      isItalic ? 'italic' : 'normal',
          textDecoration: elementStyle?.underline ? 'underline' : 'none',
        }}>
          {hasPrefix && (
            <span style={{
              flexShrink: 0, padding: '0 2px 0 8px',
              color:      fieldData.prefixStyle?.color      || elementStyle?.color || '#1e1e2e',
              fontSize:   (fieldData.prefixStyle?.fontSize  ?? elementStyle?.fontSize ?? 12) * renderRatio,
              fontFamily: fieldData.prefixStyle?.fontFamily || elementStyle?.fontFamily || 'Inter',
              fontWeight: fieldData.prefixStyle?.bold ? 'bold' : 'normal',
              fontStyle:  fieldData.prefixStyle?.italic ? 'italic' : 'normal',
            }}>
              {fieldData.prefix}
            </span>
          )}
          <span style={{
            flex:      hasPrefix ? '0 1 auto' : undefined,
            padding:   hasPrefix ? '0' : '0 8px',
            whiteSpace: 'nowrap',
            textAlign:  elementStyle?.textAlign || 'left',
          }}>
            {displayText || fieldData.placeholder || ''}
          </span>
        </div>
      </div>
    );
  }

  if (element.type === 'text') {
    const textData  = element.data as { content?: string };
    const textValue = applyForceCase(textData.content || '', elementStyle?.forceCase);
    return (
      <div style={wrapStyle}>
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', flexDirection: 'column',
          justifyContent: verticalAlignToFlex(elementStyle?.verticalAlign),
          fontFamily:     elementStyle?.fontFamily || 'Inter',
          fontSize:       fontSizePx,
          color:          elementStyle?.color || '#1e1e2e',
          fontWeight:     elementStyle?.bold ? 'bold' : 'normal',
          fontStyle:      elementStyle?.italic ? 'italic' : 'normal',
          textDecoration: elementStyle?.underline ? 'underline' : 'none',
        }}>
          <span style={{ padding: '0 8px', whiteSpace: 'nowrap', textAlign: elementStyle?.textAlign || 'left' }}>
            {textValue}
          </span>
        </div>
      </div>
    );
  }

  return null;
}

interface Props {
  template: CardTemplateData;
  fieldValues: Record<string, string>;
}

export function CardTemplatePreview({ template, fieldValues }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewScaleFactor, setPreviewScaleFactor] = useState(0);

  const recompute = useCallback(() => {
    const width = containerRef.current?.offsetWidth ?? 0;
    if (width > 0) setPreviewScaleFactor(width / template.canvas.width);
  }, [template.canvas.width]);

  useEffect(() => {
    recompute();
    const observer = new ResizeObserver(recompute);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [recompute]);

  const builderScaleFactor = BUILDER_IMG_WIDTH / template.page_w;
  const renderRatio        = previewScaleFactor > 0 ? previewScaleFactor / builderScaleFactor : 0;

  const canvasBuilderWidth  = template.canvas.width  * builderScaleFactor;
  const canvasBuilderHeight = template.canvas.height * builderScaleFactor;

  const cropWidth  = Math.round(template.canvas.width  * previewScaleFactor);
  const cropHeight = Math.round(template.canvas.height * previewScaleFactor);
  const imageWidth = Math.round(template.page_w        * previewScaleFactor);
  const offsetX    = Math.round(-(template.canvas.left * previewScaleFactor));
  const offsetY    = Math.round(-(template.canvas.top  * previewScaleFactor));

  const flatElements = flattenElements(template.elements, canvasBuilderWidth, canvasBuilderHeight);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {previewScaleFactor > 0 && (
        <div style={{
          position: 'relative',
          width:    cropWidth,
          height:   cropHeight,
          overflow: 'hidden',
          borderRadius: '4px',
          boxShadow: '0 1px 8px rgba(0,0,0,0.15)',
          backgroundColor: '#ffffff',
          ...(template.image_url ? {
            backgroundImage:    `url(${template.image_url})`,
            backgroundSize:     `${imageWidth}px auto`,
            backgroundPosition: `${offsetX}px ${offsetY}px`,
            backgroundRepeat:   'no-repeat',
          } : {}),
        }}>
          {flatElements.map((item, index) => (
            <ElementRenderer
              key={`${item.element.id}-${index}`}
              item={item}
              renderRatio={renderRatio}
              fieldValues={fieldValues}
            />
          ))}
        </div>
      )}
    </div>
  );
}
